
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Import icons
import { 
  Ruler, Scale, Container, Thermometer, SquareAsterisk, 
  Gauge, Zap, Power, Move, Clock, 
  Timer, Compass, Fuel
} from 'lucide-react';

const Converters = () => {
  const converters = [
    {
      title: "Length Converter",
      description: "Convert between different units of length and distance.",
      path: "/converters/length",
      icon: Ruler
    },
    {
      title: "Weight Converter",
      description: "Convert between different units of weight and mass.",
      path: "/converters/weight",
      icon: Scale
    },
    {
      title: "Volume Converter",
      description: "Convert between different units of volume and capacity.",
      path: "/converters/volume",
      icon: Container
    },
    {
      title: "Temperature Converter",
      description: "Convert between Celsius, Fahrenheit, Kelvin and more.",
      path: "/converters/temperature",
      icon: Thermometer
    },
    {
      title: "Area Converter",
      description: "Convert between square meters, acres, hectares and more.",
      path: "/converters/area",
      icon: SquareAsterisk
    },
    {
      title: "Pressure Converter",
      description: "Convert between pascals, bars, PSI and more.",
      path: "/converters/pressure",
      icon: Gauge
    },
    {
      title: "Energy Converter",
      description: "Convert between joules, calories, kilowatt-hours and more.",
      path: "/converters/energy",
      icon: Zap
    },
    {
      title: "Power Converter",
      description: "Convert between watts, horsepower and more.",
      path: "/converters/power",
      icon: Power
    },
    {
      title: "Force Converter",
      description: "Convert between newtons, pound-force and more.",
      path: "/converters/force",
      icon: Move
    },
    {
      title: "Time Converter",
      description: "Convert between seconds, minutes, hours, days and more.",
      path: "/converters/time",
      icon: Clock
    },
    {
      title: "Speed Converter",
      description: "Convert between mph, km/h, m/s and more.",
      path: "/converters/speed",
      icon: Timer
    },
    {
      title: "Angle Converter",
      description: "Convert between degrees, radians and more.",
      path: "/converters/angle",
      icon: Compass
    },
    {
      title: "Fuel Consumption Converter",
      description: "Convert between mpg, L/100km and more.",
      path: "/converters/fuel",
      icon: Fuel
    }
  ];

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-6">Unit Converters</h1>
        <p className="text-gray-600 mb-8">
          Our comprehensive collection of unit converters helps you convert between different measurement 
          units quickly and accurately. Choose a converter from the list below to get started.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {converters.map((converter, index) => (
            <Link key={index} to={converter.path} className="no-underline text-inherit">
              <Card className="h-full hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center mb-2">
                    <converter.icon className="w-5 h-5 mr-2 text-primary" />
                    <CardTitle className="text-lg">{converter.title}</CardTitle>
                  </div>
                  <CardDescription>{converter.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-primary text-sm font-medium">Use this converter →</div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="text-xl font-semibold mb-4">How to use our converters</h2>
          <div className="prose max-w-none">
            <p className="text-gray-700">
              Using our unit converters is simple:
            </p>
            <ol className="list-decimal list-inside text-gray-700 mt-4">
              <li>Enter the value you want to convert in the "From" field</li>
              <li>Select the unit you're converting from in the dropdown</li>
              <li>Select the unit you want to convert to in the "To" dropdown</li>
              <li>The converted result will appear automatically</li>
              <li>Click the swap button (⇅) to reverse the conversion direction</li>
            </ol>
          </div>
        </div>

        <div className="mt-12 mb-8">
          <h2 className="text-xl font-semibold mb-4">Why use our converter tools?</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Accurate conversions based on precise conversion factors</li>
            <li>User-friendly interface with instant results</li>
            <li>Comprehensive range of measurement units</li>
            <li>Mobile-friendly design for conversions on the go</li>
            <li>No installation required - works right in your browser</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Converters;
